export type CprBoundaryEditHistoryStatus = {
  canUndo: boolean;
  canRedo: boolean;
};

type CprBoundaryEditHistoryHandler = {
  undo: () => boolean;
  redo: () => boolean;
  canUndo: () => boolean;
  canRedo: () => boolean;
};

const handlers = new Map<string, CprBoundaryEditHistoryHandler>();
const listeners = new Set<() => void>();
let activeKey: string | null = null;

const getFallbackKey = () => {
  const first = handlers.keys().next();
  return first.done ? null : first.value;
};

const getHandler = (key?: string | null) => {
  const resolvedKey = key ?? activeKey ?? getFallbackKey();
  return resolvedKey ? handlers.get(resolvedKey) : undefined;
};

export const notifyCprBoundaryEditHistoryChanged = () => {
  listeners.forEach((listener) => listener());
};

export const registerCprBoundaryEditHistoryHandler = (
  key: string,
  handler: CprBoundaryEditHistoryHandler,
) => {
  handlers.set(key, handler);
  if (!activeKey) activeKey = key;
  notifyCprBoundaryEditHistoryChanged();

  return () => {
    if (handlers.get(key) !== handler) return;
    handlers.delete(key);
    if (activeKey === key) activeKey = getFallbackKey();
    notifyCprBoundaryEditHistoryChanged();
  };
};

export const setActiveCprBoundaryEditHistoryKey = (key: string) => {
  if (!handlers.has(key) || activeKey === key) return;
  activeKey = key;
  notifyCprBoundaryEditHistoryChanged();
};

export const subscribeCprBoundaryEditHistory = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getCprBoundaryEditHistoryStatus = (
  key?: string | null,
): CprBoundaryEditHistoryStatus => {
  const handler = getHandler(key);
  return {
    canUndo: !!handler?.canUndo(),
    canRedo: !!handler?.canRedo(),
  };
};

export const undoCprBoundaryEdit = (key?: string | null) => {
  const handled = !!getHandler(key)?.undo();
  if (handled) notifyCprBoundaryEditHistoryChanged();
  return handled;
};

export const redoCprBoundaryEdit = (key?: string | null) => {
  const handled = !!getHandler(key)?.redo();
  if (handled) notifyCprBoundaryEditHistoryChanged();
  return handled;
};
